import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, CheckCircle2, Info, Clock, FileDown, ChevronRight, Landmark } from "lucide-react";
import { api, errMsg, eur, fmtDateTime } from "@/lib/api";
import { Loader, Chip } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PO_STATUS = {
  transferred: ["Ausgezahlt", "mint"], released: ["Ausgezahlt", "mint"],
  pending: ["Ausstehend", "info"], held: ["In Prüfung", "warn"],
};
const MONTH_LABEL = (m) => {
  const [y, mo] = m.split("-");
  return `${["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"][+mo - 1]} ${y}`;
};

function DetailRow({ label, value, strong, tone, hint }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-line last:border-0">
      <span className={strong ? "font-bold" : "w-muted"}>{label}{hint && <span className="block text-xs w-muted font-normal">{hint}</span>}</span>
      <span className={`tabular-nums ${strong ? "font-bold" : ""} ${tone === "mint" ? "text-mint-ink" : tone === "warn" ? "text-[var(--w-warn)]" : ""}`}>{value}</span>
    </div>
  );
}

function IbanCard({ acct, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [iban, setIban] = useState("");
  const [holder, setHolder] = useState(acct?.account_holder || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/provider/me/payout-account", { iban, account_holder: holder });
      toast.success("Auszahlungskonto gespeichert");
      setEdit(false); setIban(""); onSaved(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="w-card p-5 space-y-3" data-testid="payout-iban-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-mint-soft text-mint-ink grid place-items-center"><Landmark size={20} aria-hidden="true" /></div>
        <div>
          <div className="font-bold">Auszahlungskonto (IBAN)</div>
          <div className="w-muted text-sm">Deine 75 % werden per Überweisung auf dieses Konto ausgezahlt.</div>
        </div>
      </div>
      {!edit && acct?.has_iban ? (
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-mono text-sm" data-testid="payout-iban-value">{acct.iban_masked}</div>
            {acct.account_holder && <div className="w-muted text-xs">{acct.account_holder}</div>}
          </div>
          <button className="w-btn w-btn-secondary !min-h-[40px] !px-3" onClick={() => setEdit(true)} data-testid="payout-iban-edit">Ändern</button>
        </div>
      ) : (
        <div className="space-y-3">
          {!acct?.has_iban && !edit && (
            <div className="flex items-start gap-2 text-sm bg-[var(--w-warn-soft)] rounded-xl p-3" data-testid="payout-iban-missing">
              <Info size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>Bitte hinterlege deine IBAN, damit wir dir deine Auszahlungen überweisen können.</span>
            </div>
          )}
          {(edit || !acct?.has_iban) && (
            <>
              <label className="block text-sm">
                <span className="w-label">Kontoinhaber</span>
                <input className="w-input" value={holder} onChange={(e) => setHolder(e.target.value)} placeholder="Vor- und Nachname" data-testid="payout-holder-input" />
              </label>
              <label className="block text-sm">
                <span className="w-label">IBAN</span>
                <input className="w-input font-mono" value={iban} onChange={(e) => setIban(e.target.value.toUpperCase())} placeholder="DE00 0000 0000 0000 0000 00" data-testid="payout-iban-input" />
              </label>
              <div className="flex gap-2">
                <button className="w-btn w-btn-primary" onClick={save} disabled={busy || !iban || !holder} data-testid="payout-iban-save">{busy ? "Speichern…" : "Speichern"}</button>
                {acct?.has_iban && <button className="w-btn w-btn-secondary" onClick={() => { setEdit(false); setIban(""); }} disabled={busy}>Abbrechen</button>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProviderPayouts() {
  const [acct, setAcct] = useState(null);
  const [po, setPo] = useState(null);
  const [month, setMonth] = useState("");
  const [dl, setDl] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const load = () => {
    api.get("/provider/me/payout-account").then((r) => setAcct(r.data)).catch(() => setAcct({ has_iban: false }));
    api.get("/provider/me/payouts").then((r) => { setPo(r.data); if (r.data.months?.length) setMonth(r.data.months[0]); }).catch(() => setPo({ items: [], summary: { paid_out: 0, pending: 0, count: 0 }, months: [] }));
  };
  useEffect(() => { load(); }, []);

  const download = async () => {
    if (!month) return;
    setDl(true);
    try {
      const res = await api.get(`/provider/me/payouts/statement?month=${month}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `washly-auszahlungen-${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); } finally { setDl(false); }
  };

  const openDetail = async (id) => {
    setDetailOpen(true); setDetail(null);
    try { const { data } = await api.get(`/provider/me/payouts/${id}`); setDetail(data); }
    catch (e) { setDetail({ error: errMsg(e) }); }
  };

  if (acct === null) return <Loader />;

  return (
    <div className="space-y-5 max-w-2xl" data-testid="provider-payouts">
      <div className="w-card p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-mint-soft text-mint-ink grid place-items-center"><Wallet size={20} aria-hidden="true" /></div>
          <div>
            <div className="font-bold text-lg">Deine Auszahlungen</div>
            <div className="w-muted text-sm">Du erhältst immer <b>75 %</b> jeder bezahlten Buchung – ohne Abzug von Zahlungsgebühren.</div>
          </div>
        </div>
        <p className="w-muted text-sm leading-relaxed">
          Washly behält 25 % als Vermittlungsprovision und trägt davon sämtliche Zahlungsgebühren. Dein Anteil
          wird nach vollständigem Auftragsabschluss und beidseitig bestätigter Rückgabe per Überweisung auf dein
          hinterlegtes Konto ausgezahlt.
        </p>
      </div>

      <IbanCard acct={acct} onSaved={(d) => setAcct(d)} />

      {po && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="w-card p-4" data-testid="payouts-sum-paid">
              <p className="w-label flex items-center gap-1"><CheckCircle2 size={14} aria-hidden="true" /> Ausgezahlt</p>
              <p className="text-2xl font-extrabold tabular-nums text-mint-ink">{eur(po.summary.paid_out)}</p>
            </div>
            <div className="w-card p-4" data-testid="payouts-sum-pending">
              <p className="w-label flex items-center gap-1"><Clock size={14} aria-hidden="true" /> Ausstehend</p>
              <p className="text-2xl font-extrabold tabular-nums">{eur(po.summary.pending)}</p>
            </div>
          </div>
          <div className="w-card overflow-hidden">
            <div className="px-5 py-3 border-b border-line flex items-center justify-between gap-3 flex-wrap">
              <span className="font-bold">Auszahlungsverlauf</span>
              {po.months.length > 0 && (
                <div className="flex items-center gap-2">
                  <select className="w-input !h-10 !py-0 text-sm max-w-[180px]" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="payouts-month-select">
                    {po.months.map((m) => <option key={m} value={m}>{MONTH_LABEL(m)}</option>)}
                  </select>
                  <button className="w-btn w-btn-secondary !min-h-[40px] !px-3 inline-flex items-center gap-1.5" onClick={download} disabled={dl} data-testid="payouts-download-btn">
                    <FileDown size={16} aria-hidden="true" /> {dl ? "…" : "PDF"}
                  </button>
                </div>
              )}
            </div>
            {po.items.length === 0 ? (
              <p className="w-muted text-sm px-5 py-8 text-center" data-testid="payouts-empty">Noch keine Auszahlungen. Dein Anteil erscheint hier automatisch, sobald ein Auftrag abgeschlossen ist.</p>
            ) : (
              <div className="divide-y divide-line" data-testid="payouts-history">
                {po.items.map((it) => {
                  const [label, tone] = PO_STATUS[it.status] || [it.status, "neutral"];
                  return (
                    <button key={it.id} className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-mint-soft/40 transition-colors" data-testid={`payout-row-${it.id}`} onClick={() => openDetail(it.id)}>
                      <div className="min-w-0">
                        <div className="font-semibold">{it.booking_code || it.booking_id.slice(0, 8)}</div>
                        <div className="w-muted text-xs">{fmtDateTime(it.transferred_at || it.created_at)}{it.reversed_amount > 0 ? ` · −${eur(it.reversed_amount)} rückabgewickelt` : ""}</div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="tabular-nums font-bold">{eur(it.net_amount)}</span>
                        <Chip tone={tone}>{label}</Chip>
                        <ChevronRight size={16} className="w-muted" aria-hidden="true" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent data-testid="payout-detail-dialog">
          <DialogHeader><DialogTitle>Abrechnung{detail && !detail.error ? ` · ${detail.booking_code || ""}` : ""}</DialogTitle></DialogHeader>
          {!detail ? <Loader /> : detail.error ? <p className="text-[var(--w-error)]">{detail.error}</p> : (
            <div className="space-y-1 text-sm">
              {detail.order_total != null ? (
                <>
                  <DetailRow label="Vom Kunden bezahlt" value={eur(detail.order_total)} strong />
                  <DetailRow label={`Dein Anteil (${detail.provider_pct} %)`} value={eur(detail.provider_share)} tone="mint" strong />
                  <DetailRow label={`Washly-Provision (${detail.platform_pct} %)`} value={eur(detail.washly_gross)} />
                  <DetailRow label="Zahlungsgebühren" value={`${eur(detail.psp_fee)}`} hint="trägt Washly – kein Abzug von deinem Anteil" />
                  {detail.reversed_amount > 0 && <DetailRow label="Rückabgewickelt (Erstattung)" value={`−${eur(detail.reversed_amount)}`} tone="warn" />}
                  <DetailRow label="Deine Auszahlung" value={eur(detail.net_amount)} strong tone="mint" />
                </>
              ) : (
                <DetailRow label="Deine Auszahlung" value={eur(detail.net_amount)} strong tone="mint" />
              )}
              <div className="pt-3 flex items-center gap-2">
                <span className="w-muted">Status:</span>
                <Chip tone={(PO_STATUS[detail.status] || ["", "neutral"])[1]}>{detail.status_label}</Chip>
                <span className="w-muted text-xs ml-auto">{fmtDateTime(detail.transferred_at || detail.created_at)}</span>
              </div>
              {detail.note && <p className="w-muted text-xs pt-1">{detail.note}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
